<template>
  <section class="gm-map" :style="{ height: props.mapHeight ?? '500px' }">
    <base-error-backdrop v-if="isMapError" />
    <base-loader v-if="isLoader" hint="Map is loading..." />
    <div
      v-show="!isMapError"
      id="gmmap"
      ref="google_map"
      :style="{ height: props.mapHeight ?? '500px' }"
      v-bind="$attrs"
    />
    <div v-if="props.isInfo" id="gm-info">
      <div class="gm-info">
        <div class="gm-info-img">
          <img :src="props.infoObject?.img" alt="" >
        </div>
        <div class="gm-info-grid">
          <p class="text-body-1 text-bold">
            {{ props.infoObject?.title }}
          </p>
          <span class="flexbox">
            <base-button
              size="round-sm"
              round
              icon-name="base/MapPin"
              icon-size="16px"
              variant="dark"
            />
            <p class="text-body-2 text-regular text-black-monochrome-60">
              {{ props.infoObject?.subtitle }}
            </p>
          </span>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import {
  useGoogleMapHTML,
  useGoogleMapFactory,
} from "~/utilities/composables/useGoogleMaps";

const config = useRuntimeConfig();

// Google Maps init can fail transiently (blocked/slow CDN); retry a bounded number of times.
const MAX_MAP_LOAD_ATTEMPTS = 3;
const MAP_RETRY_DELAY_MS = 4000;

interface Props {
  coords: number[] | null | undefined;
  isInfo: boolean;
  infoObject?: {
    img: string | undefined;
    title: string | null | undefined;
    subtitle: string | null | undefined;
  };
  mapHeight?: string;
  noControls?: boolean;
  noGesture?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  coords: () => [-8.638616, 115.144086],
  mapHeigth: "500px",
});
const google_map = ref<HTMLElement | null>(null);
const isMapError = ref(false);
const loadCounter = ref(0);
const isLoader = ref(false);
const mapH = computed(() => props.mapHeight);

const loadMap = async () => {
  if (!google_map.value) {
    isMapError.value = true;
    return;
  }
  loadCounter.value++;
  useGoogleMapHTML().value = google_map.value;

  const latLng = new google.maps.LatLng({
    lat: props.coords[0] || -8.638616,
    lng: props.coords[1] || 115.144086,
  });

  // `useGoogleMapFactory` rejects (it never resolves falsy) when a Google Maps library fails to
  // load — e.g. `importLibrary("marker")` throwing `Could not load "marker".` when the Maps CDN is
  // blocked by an ad blocker, a CSP rule, or a flaky network. Without a catch that rejection
  // escaped as an unhandled promise, so the loader spun forever and the retry below never ran.
  let map: Awaited<ReturnType<typeof useGoogleMapFactory>> | null = null;

  try {
    map = await useGoogleMapFactory({
      mapHTML: google_map.value,
      type: "static",
      latLng: latLng,
      options: {
        noControls: true,
        isInfo: props.isInfo,
        noGesture: props.noGesture,
        customControls: !props.noControls,
      },
    });
  } catch (error) {
    console.error("Google map failed to initialize:", error);
  }

  isLoader.value = false;
  if (!map) {
    isMapError.value = true;
    retryLoadMap();
  } else {
    isMapError.value = false;
  }
};

// Retry a failed initialization a few times — these failures are usually transient (CDN hiccup,
// slow network). `loadCounter` is incremented on every attempt, so this stops after MAX_MAP_LOAD_ATTEMPTS.
function retryLoadMap() {
  if (loadCounter.value >= MAX_MAP_LOAD_ATTEMPTS) return;
  setTimeout(() => {
    loadMap();
  }, MAP_RETRY_DELAY_MS);
}

const fetchMapScript = () => {
  if (!config.public.gmAPI) {
    isMapError.value = true;
    return;
  }
  const { onLoaded } = useScriptGoogleMaps({
    apiKey: config.public.gmAPI as string,
  });

  isLoader.value = true;
  onLoaded(async (instance) => {
    (await instance.maps) as any as typeof google.maps;
    loadMap();
    // console.log(status);
  });
};

onMounted(() => {
  fetchMapScript();
});
</script>

<style scoped lang="scss">
.gm-map {
  position: relative;
  isolation: isolate;
  height: 100%;
  width: 100%;
  overflow: hidden;
  border-radius: 5px;
}
#gmmap {
  width: 100%;
}
</style>
